/**
 * Screens 03 «День», 04 «Неделя», 05 «Месяц» (salon-mockup.html day / week /
 * month). «День» is the same cards as «Сегодня» for any day, with no binding to
 * the current time: no highlight, no «Пришёл» buttons (concepts/today-approved.html,
 * phone H). The day's count and sum sit under the title; its free gaps are the
 * point of the screen.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { useAuth } from '../auth';
import { api } from '../core/api';
import { AppBar, Shell } from '../components/Shell';
import { agendaItems, useDuration } from '../components/Agenda';
import { statusClass } from '../components/Timeline';
import { Icon } from '../components/icons';
import { formatMoney } from '../i18n';
import { monthNames } from '../lib/monthNames';
import { serviceName } from '../lib/serviceName';
import {
  addDays,
  dayTitle,
  hm,
  minuteOfDay,
  mondayOf,
  monthTitle,
  timeToMinutes,
  weekTitle,
  weekday,
  ymd,
} from '../lib/time';
import type { DayView, Locale } from '../types';
import { overlapsWith, visitCount } from './today/model';
import { BreakLine, FreeLine, VisitCard, useDaySum } from './today/rows';

/**
 * Each chip opens the view around today, never the date on screen (it drifted a month per round trip).
 * ‹ › sit at the end of the chips, so the 26px title keeps the app bar (D-75).
 */
function ViewChips({ active, prev, next }: { active: 'day' | 'week' | 'month'; prev: string; next: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = ymd(new Date());
  const links = { day: `/day/${today}`, week: `/week/${mondayOf(today)}`, month: `/month/${today.slice(0, 7)}` };
  return (
    <div className="dayswitch">
      <div className="calnav">
        <div className="chips">
          {(['day', 'week', 'month'] as const).map((v) => (
            <Link key={v} to={links[v]} className={`chip${active === v ? ' on' : ''}`} style={{ textDecoration: 'none' }}>
              {t(`cal.${v}`)}
            </Link>
          ))}
        </div>
        <button type="button" className="iconbtn" aria-label="‹" onClick={() => navigate(prev)}>
          <Icon.L />
        </button>
        <button type="button" className="iconbtn" aria-label="›" onClick={() => navigate(next)}>
          <Icon.R />
        </button>
      </div>
    </div>
  );
}

function Plus({ to }: { to: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!to) return null;
  return (
    <button type="button" className="iconbtn" aria-label={t('book.title')} onClick={() => navigate(to)}>
      <Icon.plus />
    </button>
  );
}

function useExpired() {
  return useAuth().user?.business.subscription_status === 'expired';
}

/** 03 «День». */
export function DayScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { date = ymd(new Date()) } = useParams();
  const expired = useExpired();
  const duration = useDuration();
  const daySum = useDaySum();
  const q = useQuery({ queryKey: ['day', date], queryFn: () => api.day(date) });
  const day: DayView | undefined = q.data;
  const book = (start?: string) =>
    navigate(start ? `/appointments/new?start=${encodeURIComponent(start)}` : `/appointments/new?date=${date}`);
  const holding = day?.appointments.filter((a) => a.status !== 'cancelled' && a.status !== 'no_show') ?? [];
  const freeMinutes = day?.free_gaps.reduce((s, g) => s + g.minutes, 0) ?? 0;
  const short = (m: number) => t('dur.short', { h: Math.floor(m / 60), m: String(m % 60).padStart(2, '0') });

  return (
    <Shell active="calendar">
      <header className="appbar td-hd">
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="ttl ell">{dayTitle(date, locale)}</div>
          <div className="td-sum">
            {day && (visitCount(day) ? daySum(visitCount(day), Number(day.expected_revenue)) : day.working_hours ? t('today.noVisits') : t('today.dayOff'))}
          </div>
        </div>
        <Plus to={expired ? null : `/appointments/new?date=${date}`} />
      </header>
      <ViewChips active="day" prev={`/day/${addDays(date, -1)}`} next={`/day/${addDays(date, 1)}`} />
      <div className="content">
        <div className="pane pane-wide" style={{ padding: 0 }}>
          {day && (
            <div className="td-list" style={{ paddingTop: 14 }}>
              {agendaItems(day).map((item) =>
                item.kind === 'break' ? (
                  <BreakLine key={item.id} reason={item.reason} starts={item.at} ends={item.ends} />
                ) : item.kind === 'gap' ? (
                  <FreeLine key={item.at} gap={item.gap} onBook={() => !expired && book(item.gap.starts_at)} />
                ) : (
                  <VisitCard
                    key={item.appt.id}
                    appt={item.appt}
                    overlaps={overlapsWith(item.appt, day.appointments)}
                    onOpen={() => navigate(`/appointments/${item.appt.id}`)}
                  />
                ),
              )}
              {!day.working_hours && <div className="td-off">{t('day.dayOff')}</div>}
            </div>
          )}
        </div>
        <div className="pane pane-2">
          <span className="cap">{t('day.freeWindows')}</span>
          <ul className="list">
            {day?.free_gaps.map((g) => (
              <li key={g.starts_at} onClick={() => !expired && book(g.starts_at)}>
                <span className="tnum">{hm(g.starts_at)}</span>
                <span className="grow">
                  <span className="nm" style={{ fontSize: 14.5 }}>{duration(g.minutes)}</span>
                  {day.working_hours && hm(g.ends_at) === day.working_hours.closes_at.slice(0, 5) && (
                    <span className="sm ell" style={{ display: 'block' }}>{t('day.untilClose')}</span>
                  )}
                </span>
                <span style={{ color: 'var(--accent)', display: 'flex' }}>
                  <Icon.chev />
                </span>
              </li>
            ))}
          </ul>
          <hr className="hr" />
          <div className="kv"><span className="k">{t('day.count')}</span><span className="v">{holding.length}</span></div>
          <div className="kv"><span className="k">{t('day.busy')}</span><span className="v">{short(day?.booked_minutes ?? 0)}</span></div>
          <div className="kv"><span className="k">{t('day.free')}</span><span className="v">{short(freeMinutes)}</span></div>
          <div className="kv"><span className="k">{t('today.expected')}</span><span className="v">{day ? formatMoney(day.expected_revenue, locale) : ''}</span></div>
          {!expired && (
            <button type="button" className="btn pri blk" style={{ marginTop: 14 }} onClick={() => book()}>
              {t('day.book')}
            </button>
          )}
        </div>
      </div>
    </Shell>
  );
}

/** 04 «Неделя»: hour rows, a chip in the hour each visit starts (mockup weekgrid()). */
export function WeekScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { monday: param = mondayOf(ymd(new Date())) } = useParams();
  const monday = mondayOf(param);
  const expired = useExpired();
  const q = useQuery({ queryKey: ['week', monday], queryFn: () => api.week(monday) });
  const days = q.data ?? [];
  const today = ymd(new Date());
  const opens = days.filter((d) => d.working_hours).map((d) => timeToMinutes(d.working_hours!.opens_at));
  const closes = days.filter((d) => d.working_hours).map((d) => timeToMinutes(d.working_hours!.closes_at));
  const first = Math.floor((opens.length ? Math.min(...opens) : 540) / 60);
  const last = Math.ceil((closes.length ? Math.max(...closes) : 1080) / 60);
  const hours = Array.from({ length: Math.max(last - first, 1) }, (_, i) => first + i);
  const names = t('weekday', { returnObjects: true }) as string[];

  return (
    <Shell active="calendar">
      <AppBar
        title={weekTitle(monday, locale)}
        right={<Plus to={expired ? null : `/appointments/new?date=${monday}`} />}
      />
      <ViewChips active="week" prev={`/week/${addDays(monday, -7)}`} next={`/week/${addDays(monday, 7)}`} />
      <div className="content">
        <div className="pane" style={{ overflow: 'auto' }}>
          <div className="wk">
            <div className="wh" />
            {Array.from({ length: 7 }, (_, i) => {
              const d = addDays(monday, i);
              return (
                <div key={d} className={`wh${d === today ? ' today' : ''}`} onClick={() => navigate(`/day/${d}`)}>
                  <span>{names[i]}</span>
                  <b>{Number(d.slice(8))}</b>
                </div>
              );
            })}
            {hours.map((hr) => (
              <WeekRow key={hr} hr={hr} firstRow={hr === hours[0]} days={days} monday={monday} onOpen={(id) => navigate(`/appointments/${id}`)} />
            ))}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function WeekRow({
  hr,
  firstRow,
  days,
  monday,
  onOpen,
}: {
  hr: number;
  firstRow: boolean;
  days: DayView[];
  monday: string;
  onOpen: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const today = ymd(new Date());
  return (
    <>
      <div className="wt">{String(hr).padStart(2, '0')}:00</div>
      {Array.from({ length: 7 }, (_, i) => {
        const date = addDays(monday, i);
        const cell = `wc${date === today ? ' today' : ''}`;
        const d = days.find((x) => x.date === date);
        if (d && !d.working_hours) {
          return (
            <div key={i} className={cell}>
              <div className="wkchip off" style={{ borderRadius: 4 }}>
                {firstRow ? <b>{t('week.off')}</b> : null}
              </div>
            </div>
          );
        }
        const visit = d?.appointments.find(
          (a) => a.status !== 'cancelled' && ymd(a.starts_at) === d.date && Math.floor(minuteOfDay(a.starts_at) / 60) === hr,
        );
        const off = d?.time_off.find((o) => Math.floor(minuteOfDay(o.starts_at) / 60) === hr);
        return (
          <div key={i} className={cell}>
            {visit ? (
              <div className={`wkchip ${statusClass(visit)}`} role="button" tabIndex={0} onClick={() => onOpen(visit.id)}>
                <b>{visit.client_name}</b>
                <span>{serviceName(visit.service_name_ru, visit.service_name_ky, i18n.language)}</span>
              </div>
            ) : off ? (
              <div className="wkchip off">
                <b>{off.reason}</b>
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/** 05 «Месяц»: load bars on a phone, names on desktop (mockup monthgrid()). */
export function MonthScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { yyyyMm = ymd(new Date()).slice(0, 7) } = useParams();
  const q = useQuery({ queryKey: ['month', yyyyMm], queryFn: () => api.month(yyyyMm) });
  const first = `${yyyyMm}-01`;
  const lead = weekday(first);
  const today = ymd(new Date());
  const prev = addDays(first, -1).slice(0, 7);
  const next = addDays(first, 32).slice(0, 7);
  const names = t('weekday', { returnObjects: true }) as string[];

  return (
    <Shell active="calendar">
      <AppBar title={monthTitle(yyyyMm, locale)} />
      <ViewChips active="month" prev={`/month/${prev}`} next={`/month/${next}`} />
      <div className="content">
        <div className="pane">
          <div className="mo">
            {names.map((n) => (
              <div key={n} className="mh">{n}</div>
            ))}
            {Array.from({ length: lead }, (_, i) => (
              <div key={`lead${i}`} className="mc dim">
                <span className="dn">{Number(addDays(first, i - lead).slice(8))}</span>
              </div>
            ))}
            {(q.data ?? []).map((d) => {
              const off = d.working_minutes === 0;
              const load = d.working_minutes ? Math.min(100, Math.round((d.booked_minutes / d.working_minutes) * 100)) : 0;
              const { shown, more } = monthNames(d.chips, d.appointment_count);
              return (
                <div
                  key={d.date}
                  className={`mc${d.date === today ? ' today' : ''}${off ? ' off' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/day/${d.date}`)}
                >
                  <span className="dn">{Number(d.date.slice(8))}</span>
                  {shown.length > 0 ? (
                    <div className="mchips">
                      {shown.map((c, i) => (
                        <div key={i} className={`mchip ${{ booked: '', confirmed: 'confirm', completed: 'done', cancelled: '', no_show: 'noshow' }[c.status]}`}>
                          {c.client_name}
                        </div>
                      ))}
                      {more > 0 && <span className="mmore">+{more}</span>}
                    </div>
                  ) : off ? (
                    <span className="cnt">{t('month.off')}</span>
                  ) : null}
                  <span className="load">
                    <i style={{ width: `${load}%` }} />
                  </span>
                </div>
              );
            })}
          </div>
          <p className="xs desk-only" style={{ marginTop: 14 }}>{t('month.note')}</p>
        </div>
      </div>
    </Shell>
  );
}
